import { usePage, type SectionProps } from "@/flash/context";
import { A, arr, rv, str, strs } from "@/flash/ui";
import { GhostLink, Kicker, Pic, Shell, pad2 } from "./kit";

/* Lightweight editorial sections for the subpages. No WebGL, no pinning, natural scroll. */

/** Page introduction: kicker, one H1, one paragraph, optional atmospheric image */
export function SysPageIntro({ kicker, title, subtitle, image }: SectionProps) {
  const { images } = usePage();
  const img = image ? images[str(image)] : undefined;
  return (
    <section className="sx-intro relative isolate flex min-h-[56vh] items-end overflow-hidden pb-16 pt-36 md:min-h-[62vh] md:pb-24 md:pt-44">
      {img ? (
        <>
          <Pic img={img} reveal={false} className="sx-intro-img absolute inset-0 -z-20" imgClass="object-center" sizes="100vw" />
          <div className="sx-intro-veil absolute inset-0 -z-10" aria-hidden />
        </>
      ) : null}
      <Shell className="sx-sub-enter">
        <Kicker>{str(kicker)}</Kicker>
        <h1 className="sx-display-lg mt-7 max-w-4xl">{str(title)}</h1>
        {subtitle ? <p className="mt-7 max-w-[520px] text-[16px] leading-relaxed text-muted-foreground">{str(subtitle)}</p> : null}
      </Shell>
    </section>
  );
}

/** Numbered steps separated by growing hairlines */
export function SysSteps({ kicker, title, items }: SectionProps) {
  return (
    <section className="py-20 md:py-32">
      <Shell>
        <div {...rv()} className="flex flex-col gap-6">
          <Kicker>{str(kicker)}</Kicker>
          <h2 className="sx-display-sm max-w-xl">{str(title)}</h2>
        </div>
        <ol className="mt-14 grid gap-x-10 md:mt-20 md:grid-cols-3">
          {arr(items).slice(0, 3).map((s, i) => (
            <li key={i} {...rv(i)} className="flex flex-col">
              <span className="sx-rule" aria-hidden />
              <span className="mt-6 text-[13px] tabular-nums text-primary">{pad2(i + 1)}</span>
              <h3 className="font-display mt-4 text-[1.6rem] font-normal tracking-[-0.03em]">{str(s.title)}</h3>
              <p className="mt-4 max-w-sm pb-10 text-[15px] leading-relaxed text-muted-foreground">{str(s.description)}</p>
            </li>
          ))}
        </ol>
      </Shell>
    </section>
  );
}

/** One substantial image with a concise explanation and point list */
export function SysUnified({ kicker, title, subtitle, image, points }: SectionProps) {
  const { images } = usePage();
  return (
    <section className="border-t border-white/[.06] py-20 md:py-32">
      <Shell className="grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-8">
        <Pic img={images[str(image)]} className="aspect-[16/10] w-full lg:col-span-7" imgClass="object-center" sizes="(min-width:1024px) 60vw, 100vw" />
        <div className="flex flex-col gap-6 lg:col-span-5 lg:ps-6">
          <Kicker>{str(kicker)}</Kicker>
          <h2 {...rv()} className="sx-display-sm">{str(title)}</h2>
          <p {...rv(1)} className="max-w-md text-[15px] leading-relaxed text-muted-foreground">{str(subtitle)}</p>
          <ul {...rv(2)} className="mt-2 border-t border-white/[.08]">
            {strs(points).slice(0, 6).map((p) => (
              <li key={p} className="border-b border-white/[.08] py-3.5 text-[14px] text-foreground/80">{p}</li>
            ))}
          </ul>
        </div>
      </Shell>
    </section>
  );
}

/** Capability chapters: large image + text, alternating sides, natural scroll */
export function SysChapterList({ items }: SectionProps) {
  const { images } = usePage();
  return (
    <section className="pb-12 md:pb-20">
      <Shell className="flex flex-col">
        {arr(items).slice(0, 3).map((c, i) => (
          <article key={i} className="grid gap-8 border-t border-white/[.08] py-16 md:py-24 lg:grid-cols-12 lg:items-center lg:gap-8">
            <Pic img={images[str(c.image)]} className={`aspect-[4/3] w-full lg:col-span-7 ${i % 2 ? "lg:order-2" : ""}`} imgClass="object-center" sizes="(min-width:1024px) 60vw, 100vw" />
            <div className={`flex flex-col lg:col-span-5 ${i % 2 ? "lg:pe-8" : "lg:ps-8"}`}>
              <span {...rv()} className="text-[13px] tabular-nums text-primary">{pad2(i + 1)}</span>
              <h2 {...rv(1)} className="sx-display-sm mt-5">{str(c.title)}</h2>
              <p {...rv(2)} className="mt-6 max-w-md text-[15px] leading-relaxed text-muted-foreground">{str(c.description)}</p>
              <ul {...rv(3)} className="mt-8 flex flex-wrap gap-2">
                {strs(c.points).slice(0, 4).map((p) => (
                  <li key={p} className="rounded-[3px] border border-white/[.12] px-3 py-1.5 text-[13px] text-foreground/75">{p}</li>
                ))}
              </ul>
            </div>
          </article>
        ))}
      </Shell>
    </section>
  );
}

/** Compact value list consolidating existing claims */
export function SysValue({ kicker, title, items }: SectionProps) {
  return (
    <section className="border-t border-white/[.06] bg-white/[.012] py-20 md:py-28">
      <Shell className="grid gap-10 lg:grid-cols-12 lg:gap-8">
        <div {...rv()} className="flex flex-col gap-6 lg:col-span-4">
          <Kicker>{str(kicker)}</Kicker>
          <h2 className="sx-display-sm max-w-xs">{str(title)}</h2>
        </div>
        <div className="grid gap-x-10 sm:grid-cols-2 lg:col-span-8">
          {arr(items).slice(0, 6).map((v, i) => (
            <div key={i} {...rv(i)} className="border-t border-white/[.08] py-7">
              <h3 className="text-[16px] font-medium text-foreground">{str(v.title)}</h3>
              <p className="mt-2.5 text-[14px] leading-relaxed text-muted-foreground">{str(v.description)}</p>
            </div>
          ))}
        </div>
      </Shell>
    </section>
  );
}

/** Closing CTA band with primary + secondary destinations */
export function SysCtaBand({ title, subtitle, primary, primaryHref, secondary, secondaryHref }: SectionProps) {
  return (
    <section className="border-t border-white/[.06] py-24 md:py-36">
      <Shell className="flex flex-col items-center text-center">
        <h2 {...rv()} className="sx-display-md max-w-3xl">{str(title)}</h2>
        {subtitle ? <p {...rv(1)} className="mt-6 max-w-lg text-[16px] leading-relaxed text-foreground/70">{str(subtitle)}</p> : null}
        <div {...rv(2)} className="mt-10 flex flex-wrap items-center justify-center gap-3">
          {primary ? (
            <A to={str(primaryHref)} className="sx-btn-solid h-11 px-6 text-[14px]">
              {str(primary)}
            </A>
          ) : null}
          {secondary ? <GhostLink to={str(secondaryHref)} className="h-11 px-5 text-[14px]">{str(secondary)}</GhostLink> : null}
        </div>
      </Shell>
    </section>
  );
}

/** Lightweight 404 */
export function SysNotFound({ title, subtitle }: SectionProps) {
  return (
    <section className="flex min-h-[70vh] items-center pb-20 pt-36">
      <Shell className="sx-sub-enter">
        <Kicker>404</Kicker>
        <h1 className="sx-display-lg mt-7 max-w-3xl">{str(title)}</h1>
        <p className="mt-6 max-w-md text-[16px] text-muted-foreground">{str(subtitle)}</p>
        <div className="mt-10 flex flex-wrap gap-3">
          <A to="/" className="sx-btn-solid h-11 px-6 text-[14px]">Back to home</A>
          <GhostLink to="/contact" className="h-11 px-5 text-[14px]">Contact</GhostLink>
        </div>
      </Shell>
    </section>
  );
}